import React from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiX, FiInfo } = FiIcons;

function DebugPanel() {
  const envVars = [
    {
      name: 'VITE_NCB_BASE_URL',
      description: 'Base URL for NoCodeBackend API',
      required: true,
      value: import.meta.env.VITE_NCB_BASE_URL,
      example: 'https://openapi.nocodebackend.com'
    },
    {
      name: 'VITE_NCB_INSTANCE_ID',
      description: 'NoCodeBackend Instance ID',
      required: true,
      value: import.meta.env.VITE_NCB_INSTANCE_ID,
      example: '53830_github_cloudflare_pages'
    },
    {
      name: 'VITE_NCB_API_KEY',
      description: 'NoCodeBackend API key (Bearer token)',
      required: true,
      value: import.meta.env.VITE_NCB_API_KEY,
      example: 'your-ncb-api-key-here'
    },
    {
      name: 'VITE_DROPBOX_APP_KEY',
      description: 'Dropbox App Key (Client ID)',
      required: false,
      value: import.meta.env.VITE_DROPBOX_APP_KEY,
      example: 'your-dropbox-app-key'
    }
  ];

  const serverOnlyVars = [
    {
      name: 'DROPBOX_APP_SECRET',
      description: 'Dropbox App Secret (for server-side token refresh)',
      note: 'Should be configured in Cloudflare Pages Functions environment'
    },
    {
      name: 'DROPBOX_REFRESH_TOKEN',
      description: 'Dropbox Refresh Token (for automatic token renewal)',
      note: 'Should be configured in Cloudflare Pages Functions environment'
    }
  ];

  const getStatusIcon = (value) => {
    return value ? FiCheck : FiX;
  };

  const getStatusText = (value, required) => {
    if (value) return 'Configured';
    return required ? 'Missing (Required)' : 'Not Set (Optional)';
  };

  const getStatusColor = (value, required) => {
    if (value) return 'text-success-600';
    return required ? 'text-error-600' : 'text-warning-600';
  };

  const systemInfo = {
    nodeEnv: import.meta.env.MODE,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    language: navigator.language,
    online: navigator.onLine ? 'Online' : 'Offline',
    buildTime: new Date().toISOString(),
    userAgent: navigator.userAgent
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border p-6 space-y-8">
      {/* Header */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900">Debug Panel</h2>
        <p className="text-gray-600 mt-1">Environment configuration and system status</p>
      </div>

      {/* Frontend Environment Variables */}
      <div>
        <h3 className="text-md font-medium text-gray-900 mb-4">Frontend Environment Variables</h3>
        <div className="grid gap-4">
          {envVars.map(envVar => (
            <div key={envVar.name} className="bg-gray-50 rounded-lg p-4">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center space-x-2 mb-1">
                    <code className="text-sm font-mono bg-white px-2 py-1 rounded border">
                      {envVar.name}
                    </code>
                    {envVar.required && (
                      <span className="px-2 py-1 text-xs font-medium bg-error-100 text-error-800 rounded">
                        Required
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{envVar.description}</p>
                  <p className="text-xs text-gray-500">
                    Example: <code className="bg-white px-1 rounded">{envVar.example}</code>
                  </p>
                </div>
                <div className="flex items-center space-x-2 ml-4">
                  <SafeIcon 
                    icon={getStatusIcon(envVar.value)} 
                    className={`h-4 w-4 ${getStatusColor(envVar.value, envVar.required)}`} 
                  />
                  <span className={`text-sm font-medium ${getStatusColor(envVar.value, envVar.required)}`}>
                    {getStatusText(envVar.value, envVar.required)}
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Server-Only Variables */}
      <div>
        <h3 className="text-md font-medium text-gray-900 mb-4">Server-Only Variables</h3>
        <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <SafeIcon icon={FiInfo} className="h-5 w-5 text-warning-600 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-sm text-warning-800 font-medium">Server Environment Only</p>
              <p className="text-sm text-warning-700 mt-1">
                These variables must be set in Cloudflare Pages Functions environment, not in frontend code for security reasons.
              </p>
            </div>
          </div>
        </div>
        <div className="space-y-3">
          {serverOnlyVars.map(variable => (
            <div key={variable.name} className="bg-gray-50 rounded-lg p-4">
              <code className="text-sm font-mono bg-white px-2 py-1 rounded border">
                {variable.name}
              </code>
              <p className="text-sm text-gray-600 mt-2">{variable.description}</p>
              <p className="text-xs text-gray-500 mt-1">{variable.note}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Configuration Instructions */}
      <div>
        <h3 className="text-md font-medium text-gray-900 mb-4">Configuration Instructions</h3>
        <div className="bg-blue-50 rounded-lg p-4">
          <ol className="text-sm text-blue-800 space-y-2 list-decimal list-inside">
            <li>Go to your Cloudflare Pages project dashboard</li>
            <li>Navigate to Settings → Environment Variables</li>
            <li>Add the missing environment variables with their appropriate values</li>
            <li>Redeploy the application for changes to take effect</li>
          </ol>
        </div>
      </div>

      {/* System Information */}
      <div>
        <h3 className="text-md font-medium text-gray-900 mb-4">System Information</h3>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">Environment:</span>
              <span className="font-mono">{systemInfo.nodeEnv}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Timezone:</span>
              <span className="font-mono">{systemInfo.timezone}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Language:</span>
              <span className="font-mono">{systemInfo.language}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Connection:</span>
              <span className={`font-mono ${systemInfo.online === 'Online' ? 'text-success-600' : 'text-error-600'}`}>
                {systemInfo.online}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Build Time:</span>
              <span className="font-mono text-xs">{systemInfo.buildTime}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">User Agent:</span>
              <span 
                className="font-mono text-xs truncate max-w-40" 
                title={systemInfo.userAgent}
              >
                {systemInfo.userAgent}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DebugPanel;